
import { useForm } from "vee-validate";
import { defineComponent, onMounted, ref, watch, computed } from "vue";
import { ErrorMessage, Field, Form } from "vee-validate";
import {
  getWasteType,
  getGroupList,
  getCompany,
  AddAddressGroup,
  getAddressList,
  AddAddress,
} from "@/store/api/devices";
import { forkJoin } from "rxjs";
import * as _ from "lodash";
import * as Yup from "yup";
import { addRepoToPlatform } from "@/store/api/devices";
import Swal from "sweetalert2/dist/sweetalert2.js";

interface CardData {
  nameOnCard: string;
  name_prefix: string;
  bin_height: string;
  expirationYear: string;
  cvv: string;
}

export default defineComponent({
  name: "bin-modal",
  emit: ["reload"],
  props: {
    devices: { type: Array },
    customer_id: { type: String },
  },
  components: {
    ErrorMessage,
    Field,
    Form,
  },
  setup(props, { emit }) {
    const submitButtonRef = ref<null | HTMLButtonElement>(null);
    const binModalRef = ref<null | HTMLElement>(null);
    const wasteType = ref<any>([]);
    const groupList = ref<any>([]);
    let addressList = ref<any>([]);
    let companyData = ref<any>([]);
    let checkKPI = ref<boolean>(false);

    const addToPlatformResp = ref<any>({
      success: false,
      data: {
        added: [],
        errors: [],
        skipped: [],
      },
    });
    const devicesList = ref(props.devices);

    const options = ref(["Option 1", "Option 2", "Option 3"]); // Initial options

    const newOption = ref("");
    const newGroup = ref("");
    const addingOption = ref(false);
    const addingGroupOption = ref(false);
    let newOptionAdded = false;
    let newGroupAdded = false;

    const customer_id = ref(props.customer_id);
    let response: any = {};
    const cardData = ref({
      name_prefix: null,
      bin_height: null,
      address: "",
      lat: null,
      lng: null,
      waste_type_id: null,
      pickup_threshold: null,
      group_id: null,
      address_id: null,
      weight_per_commodity: null,
      bin_capacity: null,
      bin_type: null,
      sensor_types_id: 1,
      battery: null,
      default_radius: null,
      gps_validity: null,
      fence_status: null,
      motion_status: null,
      min_measurable_distance: null,
      max_measurable_distance: null,
      routing_routing_volume: null,
      perc_threshold: null,
      abs_threshold: null,
      min_fill_level: null,
    });
    onMounted(async () => {
      wasteType.value = await getWasteType().then((res) => res.data.data);
    });
    const validationSchema = computed(() => {
      const type = (val) => Number(val);

      return Yup.object({
        sensor_types_id: Yup.string().required("Sensor type is required"),

        name_prefix: Yup.string().required("Name is required"),
        lat: Yup.string().required("Latitude is required"),
        lng: Yup.string().required("Longitude is required"),
        battery: Yup.number().required("Battery is required"),

        // ✅ Address (not tracker)
        address: Yup.string().when("sensor_types_id", {
          is: (val) => type(val) !== 2,
          then: (s) => s.required("Address is required"),
          otherwise: (s) => s.nullable().notRequired(),
        }),

        // ✅ Tracker only
        default_radius: Yup.number().when("sensor_types_id", {
          is: (val) => type(val) === 2,
          then: (s) => s.required("Default radius is required"),
          otherwise: (s) => s.nullable().notRequired(),
        }),

        gps_validity: Yup.string().when("sensor_types_id", {
          is: (val) => type(val) === 2,
          then: (s) => s.required("GPS validity required"),
          otherwise: (s) => s.nullable().notRequired(),
        }),

        fence_status: Yup.string().when("sensor_types_id", {
          is: (val) => type(val) === 2,
          then: (s) => s.required("Fence status required"),
          otherwise: (s) => s.nullable().notRequired(),
        }),

        motion_status: Yup.string().when("sensor_types_id", {
          is: (val) => type(val) === 2,
          then: (s) => s.required("Motion status required"),
          otherwise: (s) => s.nullable().notRequired(),
        }),

        // ✅ Pickup threshold (NOT 4,5)
        pickup_threshold: Yup.number().when("sensor_types_id", {
          is: (val) => ![3,4, 5].includes(type(val)),
          then: (s) => s.required("Pickup threshold required"),
          otherwise: (s) => s.nullable().notRequired(),
        }),

        // ✅ Waste type (1,3,5)
        waste_type_id: Yup.string().when("sensor_types_id", {
          is: (val) => [1, 3, 5].includes(type(val)),
          then: (s) => s.required("Waste type required"),
          otherwise: (s) => s.nullable().notRequired(),
        }),

        // ✅ Routing (1,2,3,5)
        routing_volume: Yup.number().when("sensor_types_id", {
          is: (val) => [1, 2, 3, 5].includes(type(val)),
          then: (s) => s.required("Routing volume required"),
          otherwise: (s) => s.nullable().notRequired(),
        }),

        // ✅ Distance (1,2,5)
        min_measurable_distance: Yup.number().when("sensor_types_id", {
          is: (val) => [1, 2, 5].includes(type(val)),
          then: (s) =>
            s
              .required("Min distance required")
              .test(
                "is-less-than-max",
                "Min distance must be less than max distance",
                function (value) {
                  const { max_measurable_distance } = this.parent;
                  return value < max_measurable_distance;
                }
              ),
        }),

        max_measurable_distance: Yup.number().when("sensor_types_id", {
          is: (val) => [1, 2, 5].includes(type(val)),
          then: (s) => s.required("Max distance required"),
          otherwise: (s) => s.nullable().notRequired(),
        }),

        // ✅ Bin height (1,5)
        bin_height: Yup.number().when("sensor_types_id", {
          is: (val) => [1, 5].includes(type(val)),
          then: (s) => s.required("Bin height required"),
          otherwise: (s) => s.nullable().notRequired(),
        }),

        // ✅ KPI fields (checkKPI + 1,5)
        address_id: Yup.number().when("sensor_types_id", {
          is: (val) => checkKPI.value && [1, 5].includes(val),
          then: (s) => s.required("Select Address  is required"),
          otherwise: (s) => s.nullable().notRequired(),
        }),
        group_id: Yup.number().when("sensor_types_id", {
          is: (val) => checkKPI.value && [1, 5].includes(type(val)),
          then: (s) => s.required("Bin Type is required"),
          otherwise: (s) => s.nullable().notRequired(),
        }),
        bin_type: Yup.number().when("sensor_types_id", {
          is: (val) => checkKPI.value && [1, 5].includes(type(val)),
          then: (s) => s.required("Bin Type is required"),
          otherwise: (s) => s.nullable().notRequired(),
        }),
        weight_per_commodity: Yup.number().when("sensor_types_id", {
          is: (val) => checkKPI.value && [1, 5].includes(type(val)),
          then: (s) => s.required("Weight per commodity is required"),
          otherwise: (s) => s.nullable().notRequired(),
        }),
        bin_capacity: Yup.number().when("sensor_types_id", {
          is: (val) =>checkKPI.value && [1, 5].includes(type(val)),
          then: (s) => s.required("Bin Capacity required"),
          otherwise: (s) => s.nullable().notRequired(),
        }),
        perc_threshold: Yup.number().when("sensor_types_id", {
          is: (val) => checkKPI.value && [1, 5].includes(type(val)),
          then: (s) => s.required("Percentage threshold required"),
          otherwise: (s) => s.nullable().notRequired(),
        }),

        abs_threshold: Yup.number().when("sensor_types_id", {
          is: (val) => checkKPI.value && [1, 5].includes(type(val)),
          then: (s) => s.required("Absolute threshold required"),
          otherwise: (s) => s.nullable().notRequired(),
        }),

        min_fill_level: Yup.number().when("sensor_types_id", {
          is: (val) => checkKPI.value && [1, 5].includes(type(val)),
          then: (s) => s.required("Min fill level required"),
          otherwise: (s) => s.nullable().notRequired(),
        }),
      });
    });
    const validationSchemaTrack = Yup.object().shape({
      sensor_types_id: Yup.number().required().label("Sensor Type").nullable(),
      name_prefix: Yup.string().required().label("Name").nullable(),
      lat: Yup.number().required().label("Latitude").nullable(),
      lng: Yup.number().required().label("Longitude").nullable(),
      battery: Yup.number().required().label("Battery is required").nullable(),
      default_radius: Yup.number()
        .required()
        .label("Default Radius is required")
        .nullable(),
      fence_status: Yup.number()
        .required()
        .label("Fence Status is required")
        .nullable(),
      gps_validity: Yup.number()
        .required()
        .label("GPS Validity is required")
        .nullable(),
      motion_status: Yup.number()
        .required()
        .label("Motion Status is required")
        .nullable(),
      routing_volume: Yup.number()
        .required()
        .label("routing_volume")
        .nullable(),
      min_fill_level: Yup.number()
        .nullable()
        .required("Pickup minimum fill level threshold is required"),
      abs_threshold: Yup.number()
        .nullable()
        .required("Pickup absolute threshold is required"),
      perc_threshold: Yup.number()
        .nullable()
        .required("Pickup Threshold is required")
        .typeError("Pickup Threshold must be a number"),
      max_measurable_distance: Yup.number()
        .required()
        .label("Maximum value is required")
        .nullable(),
      min_measurable_distance: Yup.number()
        .required()
        .label("Minimum is required")
        .nullable()
        .test(
          "is-less-than-max",
          "Minimum distance must be less than maximum distance",
          function (value) {
            const { max_measurable_distance } = this.parent;
            if (!value || !max_measurable_distance) return true; // skip if empty
            return value < max_measurable_distance;
          }
        ),
    });

    const validationSchemaDevices = Yup.object().shape({
      sensor_types_id: Yup.number().required().label("Sensor Type").nullable(),
      name_prefix: Yup.string().required().label("Name").nullable(),
      // default_radius: Yup.number().required().label("Default Radius is required").nullable(),
      // gps_validity: Yup.number().required().label("GPS Validity is required").nullable(),
      // fence_status: Yup.number().required().label("Fence Status is required").nullable(),
      // motion_status: Yup.number().required().label("Motion Status is required").nullable(),
      address: Yup.string().required().label("Address is required").nullable(),
      lat: Yup.number().required().label("Latitude").nullable(),
      lng: Yup.number().required().label("Longitude").nullable(),
      // battery: Yup.number().required().label("Battery is required").nullable()
    });

    watch(
      () => props.customer_id,
      (first) => {
        customer_id.value = first;
        // discardModal()
      }
    );
    const fetchData = async (newValue) => {
      try {
        companyData.value = {};
        const response = await getCompany(newValue).then((res) => {
          companyData.value = res;
          console.log("Response in fetch Function", res);
          res;
        });
        checkKPI.value = companyData.value.is_kpi;
        console.log("Check Data ", checkKPI.value);
      } catch (error) {
        console.error("Error fetching company data:", error);
      }
    };

    watch(
      () => props.customer_id,
      async (newValue, oldValue) => {
        console.log("data", newValue);
        await fetchData(newValue);
        groupList.value = await getGroupList({ customer_id: newValue }).then(
          (res) => res.data
        );
        console.log("Group List Values ", groupList.value);
      },
      { immediate: true }
    );

    const groupHandleChange = async () => {
      if (cardData.value.group_id === "add_new") {
        if (!newGroupAdded) {
          addingGroupOption.value = true;
        } else {
          cardData.value.group_id = null;
        }
      } else {
        addressList.value = await getAddressList({
          group_id: cardData.value.group_id,
        }).then((res) => res.data);

        addingGroupOption.value = false;
      }
    };

    const xyz = computed(() => {
      if (cardData.value.sensor_types_id == 1) {
        return validationSchema;
      } else if (cardData.value.sensor_types_id == 2) {
        return validationSchemaTrack;
      } else {
        return validationSchemaDevices;
      }
    });
    const addNewGroup = async () => {
      if (newGroup.value.trim() !== "") {
        let payload = {
          group_name: newGroup.value,
          se_account_id: customer_id.value,
        };
        console.log("New Option Payload", payload);
        let groupResponse = await AddAddressGroup(payload).then((res) => res);
        console.log("Group Add API Response", groupResponse);

        groupList.value.push({
          group_name: newGroup.value,
          group_id: groupResponse.id,
        });
        cardData.value.group_id = groupResponse.id; // Select the newly added option
        newGroup.value = ""; // Clear input
        addingGroupOption.value = false; // Hide input
        newGroupAdded = true;
      }
    };

    const handleChange = () => {
      if (cardData.value.address_id === "add_new") {
        if (!newOptionAdded) {
          addingOption.value = true;
        } else {
          cardData.value.address_id = "";
        }
      } else {
        addingOption.value = false;
      }
    };

    const addNewAddress = async () => {
      if (newOption.value.trim() !== "") {
        let payload = {
          address_name: newOption.value,
          group_id: cardData.value.group_id,
          lat: cardData.value.lat,
          lng: cardData.value.lng,
        };
        let addressResponse = await AddAddress(payload).then((res) => res);
        addressList.value.push({
          address_id: addressResponse.id,
          address_name: addressResponse.name,
          group_name: "",
        });
        cardData.value.address_id = addressResponse.id; // Select the newly added option
        newOption.value = ""; // Clear input
        addingOption.value = false; // Hide input
        newOptionAdded = true;
      }
    };

    const discardModal = () => {
      document.getElementById("kt_modal_bin_cancel")?.click();
    };

    const isObject = (val: boolean) => {
      return typeof val === "object";
    };

    const hideAlert = () => {
      addToPlatformResp.value = undefined;
    };
    const onInvalid = (errors) => {
  console.log("❌ FORM INVALID:", errors);
};
    const { handleSubmit } = useForm();

    const submit = handleSubmit(() => {
      console.log("**********************", submitButtonRef);
      if (!submitButtonRef.value) {
        return;
      }

      // Show loading indicator
      submitButtonRef.value.setAttribute("data-kt-indicator", "on");
      submitButtonRef.value.disabled = true; // Disable button while submitting

      let json = {
        customer_id: customer_id.value,
        devices: devicesList.value,
        detail: cardData.value,
      };

      let chunk = [...createChunks(json.devices, 50)];
      let ajax_calls = ref<any[]>([]);

      for (var i = 0; i < chunk.length; i++) {
        ajax_calls.value.push(
          addRepoToPlatform({
            devices: chunk[i],
            customer_id: json.customer_id,
            details: json.detail,
          })
        );
      }

      forkJoin(ajax_calls.value).subscribe(
        (data) => {
          for (var i = 0; i < chunk.length; i++) {
            addToPlatformResp.value = _.mergeWith(
              response,
              data[i],
              joinResults
            );
          }
          response = {};

          if (submitButtonRef.value) {
            submitButtonRef.value.disabled = false; // ✅ Re-enable button after submission
            submitButtonRef.value?.removeAttribute("data-kt-indicator");
          }

          // Reset form fields
          // cardData.value = {
          //   sensor_types_id: "",
          //   name_prefix: "",
          //   address: "",
          //   default_radius: "",
          // };

          emit("reload");
        },
        (err) => {
          console.log(err);
          if (submitButtonRef.value) {
            submitButtonRef.value.disabled = false; // ✅ Ensure button re-enables even on error
            submitButtonRef.value?.removeAttribute("data-kt-indicator");
          }
          addToPlatformResp.value = err;
        }
      );

      // your API call here
    });

    return {
      onInvalid,
      cardData,
      validationSchema,
      validationSchemaTrack,
      validationSchemaDevices,
      xyz,
      submit,
      submitButtonRef,
      binModalRef,
      isObject,
      addToPlatformResp,
      hideAlert,
      discardModal,
      wasteType,
      options,
      newOption,
      addingOption,
      handleChange,
      addNewAddress,
      newOptionAdded,
      newGroupAdded,
      newGroup,
      addingGroupOption,
      groupHandleChange,
      addNewGroup,
      groupList,
      checkKPI,
      addressList,
    };
  },
});

function* createChunks(arr, n) {
  for (let i = 0; i < arr.length; i += n) {
    yield arr.slice(i, i + n);
  }
}

function joinResults(objValue, srcValue) {
  if (_.isArray(objValue)) {
    return objValue.concat(srcValue);
  }
}
